// OptionsGeneralForm.jsx
import { useState } from "react"
import { Grid } from '@mui/material'
import "../../css/DemoCode.css"

/**
 * Options General Form is a group of data inputs usually shown when clicking on Options->General. 
 * 
 * @param {JSON} model A SWMM model in JSON format. In most cases this
 * is a 'submodel' containing only a copy of the affected sections. Using a copy allows
 * for continuous react-style editing without forcing the model to run on every keystroke.
 * @param {function} setModel Update function for updating 'model'.
 * @param {JSON} fullModel The 'full' SWMM model that contains supporting data for this component.
 * @returns UI for interactive swmm modeling.
 */
export default function OptionsGeneralForm({model, setModel, fullModel}) {
  // Process Models
  const [rainfallRunoff, setRainfallRunoff] = useState(fullModel.OPTIONS.IGNORE_RAINFALL?fullModel.OPTIONS.IGNORE_RAINFALL==='YES'?false:true:true)
  const [rdii, setRDII] = useState(fullModel.OPTIONS.IGNORE_RDII?fullModel.OPTIONS.IGNORE_RDII==='YES'?false:true:true)
  const [snowmelt, setSnowmelt] = useState(fullModel.OPTIONS.IGNORE_SNOWMELT?fullModel.OPTIONS.IGNORE_SNOWMELT==='YES'?false:true:true)
  const [groundwater, setGroundwater] = useState(fullModel.OPTIONS.IGNORE_GROUNDWATER?fullModel.OPTIONS.IGNORE_GROUNDWATER==='YES'?false:true:true)
  const [routing, setRouting] = useState(fullModel.OPTIONS.IGNORE_ROUTING?fullModel.OPTIONS.IGNORE_ROUTING==='YES'?false:true:true)
  const [quality, setQuality] = useState(fullModel.OPTIONS.IGNORE_QUALITY?fullModel.OPTIONS.IGNORE_QUALITY==='YES'?false:true:true)
  const [minSlope, setMinSlope] = useState(fullModel.OPTIONS.MIN_SLOPE?fullModel.OPTIONS.MIN_SLOPE:'0')

  // Routing Model 
  const [routingModel, setRoutingModel] = useState(fullModel.OPTIONS.FLOW_ROUTING?fullModel.OPTIONS.FLOW_ROUTING:'KINWAVE')
  
  // Infiltration Model 
  const [infiltrationModel, setInfiltrationModel] = useState(fullModel.OPTIONS.INFILTRATION?fullModel.OPTIONS.INFILTRATION:'HORTON')

  // Routing Options
  const [allowPonding, setAllowPonding] = useState(fullModel.OPTIONS.ALLOW_PONDING?fullModel.OPTIONS.ALLOW_PONDING==='YES'?true:false:false)

  const updateIgnoreRainfall = (val) => {
    setRainfallRunoff(val)
    if(model){
      setModel(prev => ({
        ...prev,
        OPTIONS:{
          ...prev.OPTIONS,
          IGNORE_RAINFALL: val===true?'NO':'YES'
        }
      }))
    }
  }

  const updateIgnoreRDII = (val) => {
    setRDII(val)
    if(model){
      setModel(prev => ({
        ...prev,
        OPTIONS:{
          ...prev.OPTIONS,
          IGNORE_RDII: val===true?'NO':'YES'
        }
      }))
    }
  }

  const updateIgnoreSnowmelt = (val) => {
    setSnowmelt(val)
    if(model){
      setModel(prev => ({
        ...prev,
        OPTIONS:{
          ...prev.OPTIONS,
          IGNORE_SNOWMELT: val===true?'NO':'YES'
        }
      }))
    }
  }

  const updateIgnoreGroundwater = (val) => {
    setGroundwater(val)
    if(model){
      setModel(prev => ({
        ...prev,
        OPTIONS:{
          ...prev.OPTIONS,
          IGNORE_GROUNDWATER: val===true?'NO':'YES'
        }
      }))
    }
  }

  const updateIgnoreRouting = (val) => {
    setRouting(val)
    if(model){
      setModel(prev => ({
        ...prev,
        OPTIONS:{
          ...prev.OPTIONS,
          IGNORE_ROUTING: val===true?'NO':'YES'
        }
      }))
    }
  }

  const updateIgnoreQuality = (val) => {
    setQuality(val)
    if(model){
      setModel(prev => ({
        ...prev,
        OPTIONS:{
          ...prev.OPTIONS,
          IGNORE_QUALITY: val===true?'NO':'YES'
        }
      }))
    }
  }

  const handleChangeFlowRouting = (val) => {
    setRoutingModel(val)
    if(model){
      setModel(prev => ({
        ...prev,
        OPTIONS:{
          ...prev.OPTIONS,
          FLOW_ROUTING: val
        }
      }))
    }
  }
  
  const handleChangeInfiltration = (val) => {
    setInfiltrationModel(val)
    if(model){
      setModel(prev => ({
        ...prev,
        OPTIONS:{
          ...prev.OPTIONS,
          INFILTRATION: val
        }
      }))
    }
  }

  const updateAllowPonding = (val) => {
    setAllowPonding(val)
    if(model){
      setModel(prev => ({
        ...prev,
        OPTIONS:{
          ...prev.OPTIONS,
          ALLOW_PONDING: val===true?'YES':'NO'
        }
      }))
    }
  }
  
  const updateMinSlope = (val) => {
    setMinSlope(val)
    if(model){
      setModel(prev => ({
        ...prev,
        OPTIONS:{
          ...prev.OPTIONS,
          MIN_SLOPE: val
        }
      }))
    }
  }


  return(
    <>
    {
      <>
      <Grid container>
        <Grid item xs={6}>
        <div style={{borderBottom:'3px solid #fff'}}>Process Models</div>
        {(
          <label className='editor-small' >
            <input  type = "checkbox"
                    style={{display: 'inline'}}
                    onChange={(e)=>{updateIgnoreRainfall(e.target.checked)}}
                    checked={rainfallRunoff}
                    id={'OptionsIgnoreRainfall'}
            />Rainfall/Runoff
          </label>
        )}
        {(
          <label className='editor-small' >
            <input  type = "checkbox"
                    style={{display: 'inline'}}
                    onChange={(e)=>{updateIgnoreRDII(e.target.checked)}}
                    checked={rdii}
                    id={'OptionsIgnoreRDII'}
            />Rainfall Dependent I/I
          </label>
        )}
        {(
          <label className='editor-small' >
            <input  type = "checkbox"
                    style={{display: 'inline'}}
                    onChange={(e)=>{updateIgnoreSnowmelt(e.target.checked)}}
                    checked={snowmelt}
                    id={'OptionsIgnoreSnowmelt'}
            />Snow Melt
          </label>
        )}
        {(
          <label className='editor-small' >
            <input  type = "checkbox"
                    style={{display: 'inline'}}
                    onChange={(e)=>{updateIgnoreGroundwater(e.target.checked)}}
                    checked={groundwater}
                    id={'OptionsIgnoreGroundwater'}
            />Groundwater
          </label>
        )}
        {(
          <label className='editor-small' >
            <input  type = "checkbox"
                    style={{display: 'inline'}}
                    onChange={(e)=>{updateIgnoreRouting(e.target.checked)}}
                    checked={routing}
                    id={'OptionsIgnoreRouting'}
            />Flow Routing
          </label>
        )}
        {(
          <label className='editor-small' >
            <input  type = "checkbox"
                    style={{display: 'inline'}}
                    onChange={(e)=>{updateIgnoreQuality(e.target.checked)}}
                    checked={quality}
                    id={'OptionsIgnoreQuality'}
            />Water Quality
          </label>
        )}
        {(
        <div>
          <div style={{borderBottom:'3px solid #fff', paddingTop: '30px'}}>Routing Model</div>
          <label className='editor-small' >
            <input
              type="radio"
              checked={routingModel === 'STEADY'}
              onChange={()=>handleChangeFlowRouting('STEADY')}
            />Steady Flow
          </label>
          <label className='editor-small' >
            <input
              type="radio"
              checked={routingModel === 'KINWAVE'}
              onChange={()=>handleChangeFlowRouting('KINWAVE')}
              />Kinematic Wave
            </label>
          <label className='editor-small' >
            <input
              type="radio"
              checked={routingModel === 'DYNWAVE'}
              onChange={()=>handleChangeFlowRouting('DYNWAVE')}
              />Dynamic Wave
            </label>
          </div>
        )}
        </Grid>
        <Grid item xs={6}>
        {(
        <div>
          <div style={{borderBottom:'3px solid #fff'}}>Infiltration Model</div>
          <label className='editor-small' >
            <input
              type="radio"
              checked={infiltrationModel === 'HORTON'}
              onChange={()=>handleChangeInfiltration('HORTON')}
            />Horton
          </label>
          <label className='editor-small' >
            <input
              type="radio"
              checked={infiltrationModel === 'MODIFIED_HORTON'}
              onChange={()=>handleChangeInfiltration('MODIFIED_HORTON')}
              />Modified Horton
          </label>
          <label className='editor-small' >
            <input
              type="radio"
              checked={infiltrationModel === 'GREEN_AMPT'}
              onChange={()=>handleChangeInfiltration('GREEN_AMPT')}
              />Green-Ampt
          </label>
          <label className='editor-small' >
            <input
              type="radio"
              checked={infiltrationModel === 'MODIFIED_GREEN_AMPT'}
              onChange={()=>handleChangeInfiltration('MODIFIED_GREEN_AMPT')}
              />Modified Green-Ampt
          </label>
          <label className='editor-small' >
            <input
              type="radio"
              checked={infiltrationModel === 'CURVE_NUMBER'}
              onChange={()=>handleChangeInfiltration('CURVE_NUMBER')}
              />Curve Number
          </label>
          </div>
        )}
        {(
          <>
          <div style={{borderBottom:'3px solid #fff', paddingTop: '30px'}}>Routing Options</div>
          <label className='editor-small' >
            <input  type = "checkbox"
                    style={{display: 'inline'}}
                    onChange={(e)=>{updateAllowPonding(e.target.checked)}}
                    checked={allowPonding}
                    id={'OptionsAllowPonding'}
            />Allow Ponding
          </label>
          <label className='editor-small' >Min Conduit Slope (%)
            <input  type = "text"
                    style={{width: '95%', display: 'flex'}}
                    onChange={(e)=>{updateMinSlope(e.target.value)}}
                    value={minSlope}
                    id={'OptionsMinSlope'}
            />
          </label>
          </>
        )}
        </Grid>
      </Grid>
      </>
    }
    </>
  )
}